import { useEffect, useRef, useState } from 'react';
import { styles } from './interactive-table-view-styles';

/**
 * A scrollable, multi-select replacement for native <select>: browsers
 * render a native select's open option list as OS chrome, which ignores
 * max-height/overflow CSS entirely - this is a plain button + absolutely-
 * positioned list instead, so the option list can actually be height-capped
 * and scrolled once there are more options than fit on screen.
 * Selecting an option toggles it in/out of the selection and keeps the list
 * open, since picking more than one value for the same column (e.g. two
 * Scope values) is a normal use case here, not a one-shot pick.
 */
export const ScrollableDropdown = ({
  label,
  selectedValues,
  options,
  onToggle,
  onClear,
}: {
  label: string;
  selectedValues: string[];
  options: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    // Use composedPath() instead of e.target: this component is mounted
    // inside the mkdocs shadow root, and a mousedown listener attached to
    // the top-level document sits outside that shadow boundary. Events
    // crossing a shadow boundary get their target retargeted to the shadow
    // host for listeners outside it, so e.target would never actually be
    // (or be contained by) anything inside the shadow tree - composedPath()
    // returns the real, un-retargeted path.
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !e.composedPath().includes(containerRef.current)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  let displayValue: string;
  if (selectedValues.length === 0) {
    displayValue = 'All';
  } else if (selectedValues.length === 1) {
    displayValue = selectedValues[0] || '(empty)';
  } else {
    displayValue = `${selectedValues.length} selected`;
  }

  return (
    <div ref={containerRef} style={styles.dropdown}>
      <button
        type="button"
        aria-label={`Filter ${label}`}
        aria-expanded={open}
        onClick={() => setOpen(prev => !prev)}
        style={styles.dropdownButton}
      >
        <span>{displayValue}</span>
        <span aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          style={styles.dropdownList}
        >
          <div
            role="option"
            tabIndex={0}
            aria-selected={selectedValues.length === 0}
            style={styles.dropdownOption}
            onClick={onClear}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') onClear();
            }}
          >
            <span aria-hidden="true">
              {selectedValues.length === 0 ? '☑' : '☐'}
            </span>
            <span style={styles.dropdownOptionLabel}>All</span>
          </div>
          {options.map(option => {
            const isSelected = selectedValues.includes(option);
            return (
              <div
                key={option}
                role="option"
                tabIndex={0}
                aria-selected={isSelected}
                style={styles.dropdownOption}
                onClick={() => onToggle(option)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') onToggle(option);
                }}
              >
                <span aria-hidden="true">{isSelected ? '☑' : '☐'}</span>
                <span style={styles.dropdownOptionLabel}>
                  {option || '(empty)'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
